/**
 * Module execution utilities for seeds and migrations.
 *
 * Centralizes the pattern of running module-defined functions
 * with fallback to auto-detected files.
 *
 * NOTE: TreeService and DagService are lazily imported to avoid circular dependency
 */

import { existsSync } from 'node:fs'
import { join } from 'node:path'
import { pathToFileURL } from 'node:url'
import type {
  ModuleManifest,
  CollectionEntityDefinition,
  TreeEntityDefinition,
  DagEntityDefinition,
  SingleEntityDefinition,
  SeedConfig,
  ModuleContext
} from '@gzl10/nexus-sdk'
// Import services that don't have circular dependency issues
import { CollectionService, SingleService } from '../../runtime/index.js'
import { getLibPath } from '@kernel/config/paths.js'
import { logger } from '@kernel/logger/index.js'

/**
 * Attempts to run a module seed.
 * 1. If mod.seed is defined, uses it directly
 * 2. Otherwise, tries to auto-detect {name}.seed.js in the module directory
 * 3. Auto-seeds 'collection', 'reference', 'tree', 'dag' entities that have seed data defined
 * 4. Auto-creates 'single' entities with defaults if record doesn't exist
 *
 * @returns true if a seed was executed, false otherwise
 */
export async function runModuleSeed(mod: ModuleManifest, ctx: ModuleContext): Promise<boolean> {
  let seeded = false

  // Seed explícito en manifest
  if (mod.seed) {
    await mod.seed(ctx)
    seeded = true
  } else {
    // Auto-detección: buscar {name}.seed.js en dist/modules/{name}/
    const seedPath = join(getLibPath(), 'dist', 'modules', mod.name, `${mod.name}.seed.js`)
    if (existsSync(seedPath)) {
      const seedModule = await import(pathToFileURL(seedPath).href)
      if (typeof seedModule.seed === 'function') {
        await seedModule.seed(ctx)
        seeded = true
      }
    }
  }

  // Auto-seed entities with seed data (collection, reference, tree, dag)
  const entitySeeds = await seedEntitiesWithData(mod, ctx)
  if (entitySeeds > 0) {
    seeded = true
  }

  return seeded
}

/**
 * Auto-seeds entities with data or defaults:
 * - 'collection', 'reference', 'tree', 'dag': entities with seed arrays
 * - 'single': entities with defaults (creates record at startup)
 * Called automatically by runModuleSeed.
 */
async function seedEntitiesWithData(mod: ModuleManifest, ctx: ModuleContext): Promise<number> {
  if (!mod.definitions?.length) {
    return 0
  }

  let totalSeeded = 0

  for (const def of mod.definitions) {
    // Collection/reference entities with seed
    if ((!def.type || def.type === 'collection' || def.type === 'reference') && hasSeedData((def as CollectionEntityDefinition).seed)) {
      const colDef = def as unknown as ConstructorParameters<typeof CollectionService>[1]
      const service = new CollectionService(ctx, colDef)
      const count = await service.seed()
      totalSeeded += count
    }

    // Tree entities with seed (lazy import to avoid circular dependency)
    if (def.type === 'tree' && hasSeedData((def as TreeEntityDefinition).seed)) {
      const { TreeService } = await import('../runtime/services/tree.service.js')
      const treeDef = def as unknown as ConstructorParameters<typeof TreeService>[1]
      const service = new TreeService(ctx, treeDef)
      const count = await service.seed()
      totalSeeded += count
    }

    // DAG entities with seed (lazy import to avoid circular dependency)
    if (def.type === 'dag' && hasSeedData((def as DagEntityDefinition).seed)) {
      const { DagService } = await import('../runtime/services/dag.service.js')
      const dagDef = def as unknown as ConstructorParameters<typeof DagService>[1]
      const service = new DagService(ctx, dagDef)
      const count = await service.seed()
      totalSeeded += count
    }

    // Single entities with defaults - persist to database at startup
    if (def.type === 'single' && (def as SingleEntityDefinition).defaults) {
      const singleDef = def as unknown as ConstructorParameters<typeof SingleService>[1]
      const service = new SingleService(ctx, singleDef)
      const count = await service.seed()
      totalSeeded += count
    }
  }

  if (totalSeeded > 0) {
    logger.info({ module: mod.name, seeded: totalSeeded }, 'Entity seeds applied')
  }

  return totalSeeded
}


/**
 * Check if seed data is defined (inline array or SeedConfig)
 */
function hasSeedData(seed: Record<string, unknown>[] | SeedConfig | undefined): boolean {
  if (!seed) return false
  // SeedConfig with URL
  if (!Array.isArray(seed) && 'source' in seed && seed.source === 'url') return true
  // Inline array
  return Array.isArray(seed) && seed.length > 0
}
